/**
 * Abrechnung — Plattform-Übersicht je Periode: Erlöse (MRR), Selbstkosten,
 * Deckungsbeitrag und Marge über alle Tenants, plus die Perioden-Aktionen
 * (abschließen, Abrechnungslauf, CSV-Export).
 *
 * Backend: GET /api/billing/overview, /periods, POST /periods/:key/close,
 * POST /runs, GET /export.csv (billing.routes.ts).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BillingOverview, BillingOverviewRow, BillingPeriod, closeBillingPeriod, downloadCsv, extractApiError,
  getBillingOverview, listBillingPeriods, runBillingPeriod,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { currentPeriodKey, formatCents, formatPercent, periodLabel } from '../services/format';
import { BillingTabs } from '../components/billing/BillingTabs';
import { PeriodPicker } from '../components/billing/PeriodPicker';
import { TypedConfirmModal } from '../components/TypedConfirmModal';

type SortKey = 'tenantSlug' | 'revenueCents' | 'costCents' | 'marginCents' | 'marginPercent';

export function BillingOverviewPage() {
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;
  const [period, setPeriod] = useState(currentPeriodKey());
  const [data, setData] = useState<BillingOverview | null>(null);
  const [periods, setPeriods] = useState<BillingPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'marginCents', dir: 1 });
  const [onlyWarnings, setOnlyWarnings] = useState(false);

  function load(): void {
    setLoading(true);
    setError(null);
    Promise.all([getBillingOverview(period), listBillingPeriods()])
      .then(([o, p]) => { setData(o); setPeriods(p.periods); })
      .catch((e) => setError(extractApiError(e)))
      .finally(() => setLoading(false));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period]);

  const periodInfo = periods.find((p) => p.key === period);
  const periodStatus = data?.periodStatus ?? periodInfo?.status ?? 'open';

  async function act(fn: () => Promise<string>): Promise<void> {
    setBusy(true); setError(null); setNotice(null);
    try { setNotice(await fn()); load(); } catch (e) { setError(extractApiError(e)); } finally { setBusy(false); }
  }

  const rows = useMemo(() => {
    const list = (data?.rows ?? []).filter((r) => !onlyWarnings || r.warnings.length > 0);
    const dir = sort.dir;
    return [...list].sort((a, b) => {
      const av = a[sort.key]; const bv = b[sort.key];
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir;
      return ((av as number | null ?? -Infinity) - (bv as number | null ?? -Infinity)) * dir;
    });
  }, [data, sort, onlyWarnings]);

  function toggleSort(key: SortKey): void {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: key === 'tenantSlug' ? 1 : -1 }));
  }

  const t = data?.totals;

  return (
    <div>
      <div className="mb-2">
        <h2 className="text-xl font-semibold">Abrechnung</h2>
        <p className="text-sm text-slate-500">
          Erlöse aus Preisliste × (Seats, Module, Nutzung), Selbstkosten aus Kostenmodell bzw. OpenCost. Perioden sind Kalendermonate;
          ein Abschluss friert die Nutzung ein und setzt Zähler zurück.
        </p>
      </div>
      <BillingTabs />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <PeriodPicker value={period} onChange={setPeriod} status={periodStatus} />
        <div className="flex flex-wrap gap-2">
          <button type="button" className="px-3 py-1 rounded border" disabled={busy} onClick={() => downloadCsv(`/billing/export.csv?period=${period}`, `abrechnung-${period}.csv`).catch((e) => setError(extractApiError(e)))}>
            CSV exportieren
          </button>
          <button type="button" className="btn-secondary" disabled={busy || isReadOnly || periodStatus === 'closed'} onClick={() => setConfirmClose(true)} title={periodStatus === 'closed' ? 'Periode bereits abgeschlossen' : 'Nutzung einfrieren und Zähler zurücksetzen'}>
            Periode abschließen
          </button>
          <button type="button" className="btn-primary" disabled={busy || isReadOnly} onClick={() => act(async () => {
            const r = await runBillingPeriod(period);
            return `Abrechnungslauf ${periodLabel(period)}: ${r.created} neu, ${r.updated} aktualisiert, ${r.skipped} übersprungen.`;
          })}>
            Abrechnungslauf starten
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">{error}</div>}
      {notice && <div className="bg-green-50 border border-green-200 text-green-800 rounded p-3 mb-4 text-sm">{notice} <Link to={`/billing/statements?period=${period}`} className="underline">Abrechnungen ansehen →</Link></div>}
      {data?.warnings.map((w) => (
        <div key={w} className="bg-amber-50 border border-amber-200 text-amber-900 rounded p-2 mb-2 text-sm">
          ⚠️ {w}{w.includes('Preisliste') && <> — <Link to="/billing/price-lists" className="underline">Preislisten</Link></>}
        </div>
      ))}

      {loading || !t ? <p className="text-slate-500">Lade…</p> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Kpi label="Erlöse (netto)" value={formatCents(t.revenueCents)} sub={`${t.billableTenants} von ${t.tenants} Tenants abrechenbar`} />
            <Kpi label="Selbstkosten" value={formatCents(t.costCents)} sub={`${data.rows.filter((r) => r.costSource === 'opencost').length}× OpenCost, sonst Modell`} />
            <Kpi label="Deckungsbeitrag" value={formatCents(t.marginCents)} tone={t.marginCents < 0 ? 'bad' : 'good'} />
            <Kpi label="Marge" value={formatPercent(t.marginPercent)} tone={t.marginPercent !== null && t.marginPercent < 20 ? 'warn' : 'good'} />
            <Kpi label="Abrechenbare Seats" value={t.seats.toLocaleString('de-DE')} sub={data.priceLists.map((p) => `${p.brand}: ${p.name ?? 'keine Preisliste'}`).join(' · ')} />
          </div>

          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Tenants ({rows.length})</h3>
            <label className="text-sm text-slate-600 flex items-center gap-2">
              <input type="checkbox" checked={onlyWarnings} onChange={(e) => setOnlyWarnings(e.target.checked)} /> nur mit Hinweisen
            </label>
          </div>
          <table className="w-full bg-white shadow rounded text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <Th onClick={() => toggleSort('tenantSlug')} active={sort.key === 'tenantSlug'}>Tenant</Th>
                <th className="p-2">Kunde</th>
                <th className="p-2">Plan</th>
                <th className="p-2 text-right">Seats</th>
                <Th onClick={() => toggleSort('revenueCents')} active={sort.key === 'revenueCents'} right>Erlös</Th>
                <Th onClick={() => toggleSort('costCents')} active={sort.key === 'costCents'} right>Kosten</Th>
                <Th onClick={() => toggleSort('marginCents')} active={sort.key === 'marginCents'} right>Deckungsbeitrag</Th>
                <Th onClick={() => toggleSort('marginPercent')} active={sort.key === 'marginPercent'} right>Marge</Th>
                <th className="p-2">Hinweise</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => <Row key={r.tenantSlug} r={r} period={period} />)}
              {rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500">Keine Tenants.</td></tr>}
            </tbody>
          </table>
        </>
      )}

      {confirmClose && (
        <TypedConfirmModal
          title={`Periode ${periodLabel(period)} abschließen?`}
          description="Friert die Nutzung aller Tenants für diese Periode ein (Spitzenwerte für Gauges, Summen für Zähler) und setzt die Zähler-Snapshots auf 0. Abrechnungen dieser Periode rechnen danach mit den eingefrorenen Werten. Nicht rückgängig machbar."
          confirmPhrase={period}
          confirmLabel="Periode abschließen"
          affectedSlugs={(data?.rows ?? []).map((r) => r.tenantSlug)}
          onCancel={() => setConfirmClose(false)}
          onConfirm={() => { setConfirmClose(false); void act(async () => { const r = await closeBillingPeriod(period); return `Periode ${periodLabel(period)} abgeschlossen: ${r.tenants} Tenants, ${r.metrics} Werte eingefroren.`; }); }}
        />
      )}
    </div>
  );
}

function Row({ r, period }: { r: BillingOverviewRow; period: string }) {
  const negative = r.marginCents < 0;
  return (
    <tr className={`border-t hover:bg-slate-50 ${!r.billable ? 'text-slate-400' : ''}`}>
      <td className="p-2 font-mono">
        <Link to={`/tenants/${r.tenantSlug}?period=${period}#billing`} className="text-primary-600 hover:underline">{r.tenantSlug}</Link>
        {!r.billable && <span className="badge bg-slate-200 text-slate-600 ml-2">nicht abrechenbar</span>}
      </td>
      <td className="p-2">{r.customer ? <Link to={`/customers/${r.customer.id}`} className="hover:underline">{r.customer.companyName}</Link> : <span className="text-amber-700">kein Kunde</span>}</td>
      <td className="p-2"><span className="badge bg-slate-200">{r.plan}</span></td>
      <td className="p-2 text-right font-mono">{r.billableSeats}</td>
      <td className="p-2 text-right font-mono">{formatCents(r.revenueCents)}</td>
      <td className="p-2 text-right font-mono" title={r.costSource === 'opencost' ? 'Ist-Kosten aus OpenCost' : 'Kostenmodell (Footprint/Nutzung × Stückkosten)'}>
        {formatCents(r.costCents)} <span className="text-xs text-slate-400">{r.costSource === 'opencost' ? 'OC' : 'M'}</span>
      </td>
      <td className={`p-2 text-right font-mono ${negative ? 'text-red-700 font-semibold' : ''}`}>{formatCents(r.marginCents)}</td>
      <td className={`p-2 text-right font-mono ${negative ? 'text-red-700' : ''}`}>{formatPercent(r.marginPercent)}</td>
      <td className="p-2 text-xs text-amber-800">{r.warnings.length > 0 ? <span title={r.warnings.join('\n')}>⚠️ {r.warnings.length}</span> : ''}</td>
    </tr>
  );
}

function Th({ children, onClick, active, right }: { children: string; onClick: () => void; active: boolean; right?: boolean }) {
  return (
    <th className={`p-2 cursor-pointer select-none ${right ? 'text-right' : ''} ${active ? 'underline' : ''}`} onClick={onClick}>{children}</th>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'good' | 'warn' | 'bad' }) {
  const color = tone === 'bad' ? 'text-red-700' : tone === 'warn' ? 'text-amber-700' : 'text-neutral-900';
  return (
    <div className="bg-white rounded shadow p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-xl font-mono mt-1 ${color}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1 truncate" title={sub}>{sub}</p>}
    </div>
  );
}
